import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { detectResourcesSync, envDetector, hostDetector, osDetector, Resource } from '@opentelemetry/resources';
import { ConsoleMetricExporter, MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { B3InjectEncoding, B3Propagator } from '@opentelemetry/propagator-b3';
import { diag, DiagConsoleLogger, DiagLogLevel, metrics } from '@opentelemetry/api';
import { v4 as uuid } from 'uuid';
import { escapeRegExp } from 'lodash';

import {
  appName,
  resourceAttributes,
  isNodeRuntime,
  isOtelEnabled,
  isOtelLogging,
  metricsIntervalMs,
  metricsUrl,
  tracesSampleRatio,
  tracesUrl,
  isJestRuntime,
} from '../constants/setup';
import { handleEvent, handleInit, handleNavigate } from '../services/event';
import { CustomLoadInstrumentation } from '../instrumentation/load';
import { PageKeyMap } from '../types/setup';
import { setupCSRTelemetryFlush } from '../processors/flush';
import '../types/global';
import { CustomFetchInstrumentation } from '../instrumentation/fetch';

const otelLogger = diag.createComponentLogger({
  namespace: '✨ @telemetry-sdk/setup/csr',
});
if (!isNodeRuntime && isOtelLogging) {
  diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.INFO);
}

let isOtelInitialized = false;

type OtelRegisterProps = {
  bffUrl: string | undefined;
  pageKeyMap: PageKeyMap;
  userId: string | undefined;
};

const otelRegister = (props: OtelRegisterProps) => {
  const { bffUrl, pageKeyMap, userId = uuid() } = props;

  // Runtime Check
  if (isJestRuntime || isNodeRuntime || isOtelInitialized || !bffUrl) {
    if (!isNodeRuntime)
      otelLogger.error(`Otel disabled for '${appName}' - CSR`, {
        isJestRuntime,
        isNodeRuntime,
        isOtelInitialized,
        bffUrl,
      });
    return;
  }
  isOtelInitialized = true;

  // Resource
  const resource = new Resource(resourceAttributes).merge(
    detectResourcesSync({
      detectors: [envDetector, hostDetector, osDetector],
    })
  );

  // Trace Provider
  const tracerProvider = new WebTracerProvider({
    resource,
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(tracesSampleRatio),
    }),
    spanProcessors: [
      ...(isOtelEnabled
        ? [
            new BatchSpanProcessor(
              new OTLPTraceExporter({
                url: tracesUrl,
                headers: {
                  'Content-Type': 'application/json',
                },
              })
            ),
          ]
        : []),
      ...(isOtelEnabled && isOtelLogging ? [new BatchSpanProcessor(new ConsoleSpanExporter())] : []),
    ],
  });

  // Metrics Provider
  const meterProvider = new MeterProvider({
    resource,
    readers: [
      ...(isOtelEnabled
        ? [
            new PeriodicExportingMetricReader({
              exporter: new OTLPMetricExporter({
                url: metricsUrl,
                headers: {
                  'Content-Type': 'application/json',
                },
              }),
              exportIntervalMillis: metricsIntervalMs,
            }),
          ]
        : []),
      ...(isOtelLogging && isOtelLogging
        ? [
            new PeriodicExportingMetricReader({
              exporter: new ConsoleMetricExporter(),
              exportIntervalMillis: 1000,
            }),
          ]
        : []),
    ],
  });

  // Propagator
  const propagator = new CompositePropagator({
    propagators: [
      new W3CTraceContextPropagator(),
      new W3CBaggagePropagator(),
      new B3Propagator({
        injectEncoding: B3InjectEncoding.SINGLE_HEADER,
      }),
    ],
  });

  // Register Instrumentation
  registerInstrumentations({
    tracerProvider,
    meterProvider,
    instrumentations: [
      new CustomLoadInstrumentation({
        hooks: {
          onLoadEvent: handleEvent,
        },
      }),
      new CustomFetchInstrumentation({
        options: {
          clearTimingResources: true,
          ignoreNetworkEvents: false,
          measureRequestSize: false,
        },
        urls: {
          propagatedUrls: [new RegExp(`^${escapeRegExp(bffUrl)}`)],
          ignoredUrls: [tracesUrl, metricsUrl],
        },
        hooks: {
          onFetchEvent: handleEvent,
        },
      }),
    ],
  });

  // Unload Handler
  setupCSRTelemetryFlush({ tracerProvider, meterProvider });

  // Register Providers
  tracerProvider.register({
    contextManager: new ZoneContextManager(),
    propagator,
  });
  metrics.setGlobalMeterProvider(meterProvider);

  // Complete
  otelLogger.info(`Otel enabled for '${appName}' - CSR`, { exporting: isOtelEnabled });

  // Init Store
  handleInit(pageKeyMap, userId);
  handleNavigate(window.location.pathname);
};

export { otelRegister };
